/**
 * useDualVariantTitle
 *
 * Sobreescribe `document.title` mientras el componente está montado para que
 * los distintos sabores del flujo "Dual CTA" sean identificables a simple
 * vista (tab del browser, history, bookmarks, page_title de PostHog,
 * link previews al compartir, etc.).
 *
 * Por qué: hoy AR-dual e INTL-dual heredan el mismo title default del
 * `<head>` ("Jua Perrone | Modelo y Content Creator"). Eso impide:
 *   - filtrar sesiones en PostHog por variante
 *   - pedirle a un usuario "qué versión viste" (todas se llaman igual)
 *   - distinguir en métricas qué experiencia performa mejor
 *
 * El hook restaura el title original al desmontar, así no contamina otras
 * rutas si el usuario navega dentro de la SPA.
 */

import { useEffect } from "react";
import { BRAND_PERSON_NAME, BRAND_NAME } from "@/lib/brandConfig";

export type DualVariant = "ar" | "global" | "us";

// Palabra "Miembros" (en vez de "Acceso") para que el title se lea como una
// sección de comunidad/membresía y no se confunda con un login técnico,
// página de soporte, etc. Lo importante es que sea friendly y descriptivo.
const VARIANT_LABEL: Record<DualVariant, string> = {
  ar:     "Miembros Argentina 🇦🇷",
  global: "Miembros Global 🌎",
  us:     "US Exclusive 🇺🇸",
};

export function useDualVariantTitle(
  variant: DualVariant | null | undefined,
  options?: { variantId?: string | null },
): void {
  const variantId = options?.variantId?.trim() || null;
  useEffect(() => {
    if (!variant) return;
    const prev = document.title;
    const personOrBrand = (BRAND_PERSON_NAME || BRAND_NAME || "").trim();
    const suffix = VARIANT_LABEL[variant];
    const variantTag = variantId ? ` [${variantId}]` : "";
    document.title = personOrBrand
      ? `${personOrBrand} · ${suffix}${variantTag}`
      : `${suffix}${variantTag}`;
    return () => {
      document.title = prev;
    };
  }, [variant, variantId]);
}
